import { useState, useEffect, useMemo, useCallback } from 'react';

/**
 * Hook for managing calendar pagination and navigation
 * 
 * @param {Array} monthsToDisplay - Array of month objects { year, month }
 * @param {number} monthsPerPage - Number of months to display per page
 * @returns {Object} - Navigation state and handlers
 */

export const useCalendarNavigation = (monthsToDisplay, monthsPerPage = 6) => {
    // Find which page contains the current month
    const findCurrentMonthPage = useCallback(() => {
        if (!monthsToDisplay || monthsToDisplay.length === 0) return 0;

        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth();

        // Find the index of the current month in the monthsToDisplay array
        const currentMonthIndex = monthsToDisplay.findIndex(
            month => month.year === currentYear && month.month === currentMonth
        );

        // If current month not found, default to first page
        if (currentMonthIndex === -1) return 0;

        // Calculate which page contains the current month
        return Math.floor(currentMonthIndex / monthsPerPage);
    }, [monthsToDisplay, monthsPerPage]);

    const [pagination, setPagination] = useState({
        currentPage: 0, // Will be updated after initial render
        monthsPerPage
    });

    // Set the initial page after monthsToDisplay is populated
    useEffect(() => {
        if (monthsToDisplay && monthsToDisplay.length > 0) {
            setPagination(prev => ({
                ...prev,
                currentPage: findCurrentMonthPage()
            }));
        }
    }, [monthsToDisplay, findCurrentMonthPage]);

    const totalPages = useMemo(() =>
        Math.ceil(monthsToDisplay.length / pagination.monthsPerPage),
        [monthsToDisplay.length, pagination.monthsPerPage]
    );

    const currentMonths = useMemo(() => {
        const startIdx = pagination.currentPage * pagination.monthsPerPage;
        return monthsToDisplay.slice(startIdx, startIdx + pagination.monthsPerPage);
    }, [monthsToDisplay, pagination.currentPage, pagination.monthsPerPage]);

    const handlePreviousPage = useCallback(() => {
        setPagination(prev => ({
            ...prev,
            currentPage: Math.max(0, prev.currentPage - 1)
        }));
    }, []);

    const handleNextPage = useCallback(() => {
        setPagination(prev => ({
            ...prev,
            currentPage: Math.min(totalPages - 1, prev.currentPage + 1)
        }));
    }, [totalPages]);

    // Group months by year for better organization
    const monthsByYear = useMemo(() => {
        const result = {};
        currentMonths.forEach(({ year, month }) => {
            if (!result[year]) {
                result[year] = [];
            }
            // Only add the month if it's not already in the array
            if (!result[year].includes(month)) {
                result[year].push(month);
            }
        });

        // Sort the months in each year to ensure correct order
        Object.keys(result).forEach(year => {
            result[year].sort((a, b) => a - b);
        });

        return result;
    }, [currentMonths]);

    return {
        currentPage: pagination.currentPage,
        totalPages,
        currentMonths,
        monthsByYear,
        handlePreviousPage,
        handleNextPage
    };
};

export default useCalendarNavigation;